import * as React from 'react'
import { useNavigate, type NavigateFunction } from 'react-router-dom'
import styled, { css } from 'styled-components'

import * as api from '@owl-nest/api-client/latest'
import * as hooks from '@owl-nest/hooks'
import { t, tp } from '@owl-nest/localize'
import * as models from '@owl-nest/models'
import * as plume from '@ulule/owl-kit-components/next'

import * as UFE from './UFE'

import {
  NavMenu,
  type AdminSectionConfiguration,
  type CoachingSectionConfiguration,
  type MidZoneConfiguration,
  type MidZoneConfigurationItem,
  type NavMenuProps,
} from './NavMenu'

export enum Screen {
  ACCOUNT = 'account',
  ANALYTICS = 'analytics',
  DASHBOARD = 'dashboard',
  DELIVERY = 'delivery',
  DISCUSSIONS = 'discussions',
  FAQ = 'faq',
  GOAL = 'goal',
  IMPACT = 'impact',
  LAUNCH = 'launch',
  LOCATION = 'location',
  MEMBERSHIPS = 'memberships',
  MEMBERS = 'members',
  MY_PROJECT = 'my-project',
  NEWS = 'news',
  OFFBOARDING = 'offboarding',
  OFFLINE_DONATIONS_LIST = 'list',
  ORDERS = 'orders',
  PRESENTATION = 'presentation',
  REWARDS = 'rewards',
  TAX_DEDUCTION = 'taxdeduction',
  TEAM = 'team',
}

export const allScreens: string[] = Object.values(Screen)

export const pureBackofficeScreens: string[] = [
  Screen.ACCOUNT,
  Screen.ANALYTICS,
  Screen.DELIVERY,
  Screen.GOAL,
  Screen.IMPACT,
  Screen.LAUNCH,
  Screen.LOCATION,
  Screen.MEMBERS,
  Screen.MEMBERSHIPS,
  Screen.NEWS,
  Screen.OFFBOARDING,
  Screen.ORDERS,
  Screen.PRESENTATION,
  Screen.REWARDS,
  Screen.TAX_DEDUCTION,
  Screen.TEAM,
]

const PURE_BACKOFFICE_PATHS = {
  ACCOUNT: `/${Screen.ACCOUNT}`,
  ANALYTICS: `/${Screen.ANALYTICS}`,
  GOAL: `/${Screen.GOAL}`,
  IMPACT: `/${Screen.IMPACT}`,
  LAUNCH: `/${Screen.LAUNCH}`,
  LOCATION: `/${Screen.LOCATION}`,
  MEMBERS: `/${Screen.MEMBERS}`,
  MEMBERSHIPS: `/${Screen.MEMBERSHIPS}`,
  NEWS: `/${Screen.NEWS}`,
  OFFBOARDING: `/${Screen.OFFBOARDING}`,
  ORDERS: `/${Screen.ORDERS}`,
  PRESENTATION: '/',
  REWARD: `/${Screen.REWARDS}`,
  TAX_DEDUCTION: `/${Screen.TAX_DEDUCTION}`,
  TEAM: `/${Screen.TEAM}`,
} as const

type ValueOf<T> = T[keyof T]
type ConfiguredNavMenuRole = ValueOf<models.project.EditingUserRole>
type ConfiguredNavMenuProps = {
  className?: string
  project: api.Project<'manager' | 'default_manager' | 'unread_count' | 'account'>
  role: ConfiguredNavMenuRole | undefined
  screen: Screen
}

function ConfiguredNavMenuComponent({
  className,
  project,
  role,
  screen,
}: ConfiguredNavMenuProps): React.ReactElement<ConfiguredNavMenuProps> {
  // Note: Only pure backoffice screens have a parent router to capture useNavigate()
  const isPureBackoffice = pureBackofficeScreens.includes(screen)
  const navigate = isPureBackoffice ? useNavigate() : undefined
  const isClientSide = hooks.useClientSide()
  const isStaff = role === 'staff' || UFE.USER.is_staff
  const isOwnerOrStaff = Boolean(role === api.UserRole.Owner || isStaff)

  return (
    <NavMenu
      className={className}
      adminSection={isStaff ? getAdminData({ project }) : undefined}
      coachingSection={isOwnerOrStaff ? getCoachingData({ project, screen }) : undefined}
      locale={UFE.USER_LOCALE}
      midZone={getMidZone({ navigate, project, role, screen, isClientSide })}
      project={project}
    />
  )
}

function getAdminData({ project }: { project: api.Project }): AdminSectionConfiguration {
  return {
    projectId: project.id,
  }
}

function getCoachingData({
  project,
  screen,
}: {
  project: api.Project<'manager' | 'default_manager' | 'unread_count'>
  screen: Screen
}): CoachingSectionConfiguration {
  return {
    coachAvatarSrc: models.image.getUrl(
      project.manager?.avatar.versions || project.default_manager?.avatar.versions,
      '128x128',
    ),
    discussionUrl: `/projects/${project.id}/discussion/`,
    isActive: screen === Screen.DISCUSSIONS,
    unreadCount: project.unread_count,
  }
}

function getMidZone({
  isClientSide,
  navigate,
  project,
  role,
  screen,
}: {
  isClientSide: boolean
  navigate: NavigateFunction | undefined
  project: api.Project<'account'>
  role: ConfiguredNavMenuRole | undefined
  screen: Screen
}): MidZoneConfiguration {
  const status = project.status
  const isOnline = project.is_online
  const isOngoing = models.project.lifecycleStatus(project) === models.project.LifecycleStatus.ONGOING
  const isRefused = status === api.ProjectStatus.REFUSED
  const isProjectDonationBased = models.project.isDonationBased(project)
  const isProjectMembership = models.project.isMembership(project)
  const isProjectRewardBased = models.project.isRewardBased(project)
  const isProjectVendorBased = models.project.isVendorBased(project)
  const projectLifecycleStatus = models.project.lifecycleStatus(project)

  const isOwner = role === api.UserRole.Owner
  const isStaff = role === 'staff' || UFE.USER.is_staff

  const isPureBackoffice = pureBackofficeScreens.includes(screen)
  const baseBackofficePath = `/projects/${project.id}/backoffice`

  const hasAccessToPreview = models.project.hasAccessToPreview(project)

  const midZone: NavMenuProps['midZone'] = []

  if (isProjectVendorBased) {
    midZone.push([
      {
        label: 'Ventes',
        icon: <plume.glyphs.stroke.Checkbox size={20} />,
        items: [
          makeMenuEntry({
            label: 'Liste des ventes',
            href: `${baseBackofficePath}${PURE_BACKOFFICE_PATHS.ORDERS}`,
            isActive: screen === Screen.ORDERS,
          }),
          makeMenuEntry({
            label: 'Facturation',
            href: `${baseBackofficePath}${PURE_BACKOFFICE_PATHS.ACCOUNT}`,
            isActive: screen === Screen.ACCOUNT,
          }),
          makeMenuEntry({
            label: 'Équipe',
            href: `${baseBackofficePath}${PURE_BACKOFFICE_PATHS.TEAM}`,
            isActive: screen === Screen.TEAM,
          }),
        ],
      },
    ])

    return midZone
  }

  const myProject: NavMenuProps['midZone'][number] = []
  const myProjectItems: MidZoneConfigurationItem[] = [
    makeMenuEntry({
      label: isProjectMembership ? tp('Edit page', 'Project Backoffice') : t('Project description'),
      href: `${baseBackofficePath}${PURE_BACKOFFICE_PATHS.PRESENTATION}`,
      isActive: screen === Screen.PRESENTATION,
    }),
  ]

  if (!isProjectMembership) {
    myProjectItems.push(
      makeMenuEntry({
        label: t('Impact and hurdles'),
        href: `${baseBackofficePath}${PURE_BACKOFFICE_PATHS.IMPACT}`,
        isActive: screen === Screen.IMPACT,
      }),
    )
    myProjectItems.push({
      label: t('FAQ'),
      href: `/projects/${project.id}/edit/faq/`,
      isActive: screen === Screen.FAQ,
    })
  }

  if (isProjectMembership) {
    myProjectItems.splice(
      1,
      0,
      makeMenuEntry({
        label: t('Memberships and donations'),
        isDisabled: isRefused,
        href: isRefused ? undefined : `${baseBackofficePath}${PURE_BACKOFFICE_PATHS.MEMBERSHIPS}`,
        isActive: screen === Screen.MEMBERSHIPS,
      }),
    )
  } else if (isProjectRewardBased) {
    myProjectItems.splice(
      1,
      0,
      makeMenuEntry({
        label: t('Rewards'),
        isDisabled: isRefused,
        href: isRefused ? undefined : `${baseBackofficePath}${PURE_BACKOFFICE_PATHS.REWARD}`,
        isActive: screen === Screen.REWARDS,
      }),
    )
  }

  if (!isProjectMembership) {
    if (isStaff || isOwner) {
      myProjectItems.splice(
        2,
        0,
        makeMenuEntry({
          label: t('Goal and duration'),
          href: `${baseBackofficePath}${PURE_BACKOFFICE_PATHS.GOAL}`,
          isActive: screen === Screen.GOAL,
        }),
      )
    }
  }

  const projectImage = models.project.mainImage(project, 'small')
  const projectTitle = models.project.name(project)

  myProject.push({
    label:
      projectTitle && projectTitle !== ''
        ? projectTitle
        : isProjectMembership
          ? t('My page')
          : tp('My project', 'Project Manager Menu'),
    hasPlainColorIcon: true,
    href: `/${project.slug}`,
    icon: projectImage ? (
      <ProjectImage alt={projectTitle} src={projectImage} type="small" />
    ) : (
      <ProjectBaseIcon project={project} />
    ),
    isActive: screen === Screen.MY_PROJECT,
    isClickable: true,
    isDisabled: isRefused,
    items: myProjectItems,
    subtitle: <ProjectStatus project={project} />,
  })

  if (isStaff || isOwner) {
    const baseSettingsEntries = [
      makeMenuEntry({
        label: tp('Team', 'Project Manager Menu'),
        href: `${baseBackofficePath}${PURE_BACKOFFICE_PATHS.TEAM}`,
        isActive: screen === Screen.TEAM,
      }),
      makeMenuEntry({
        label: t('Location'),
        href: `${baseBackofficePath}${PURE_BACKOFFICE_PATHS.LOCATION}`,
        isActive: screen === Screen.LOCATION,
      }),
      makeMenuEntry({
        label: t('Analysis tools'),
        href: `${baseBackofficePath}${PURE_BACKOFFICE_PATHS.ANALYTICS}`,
        isActive: screen === Screen.ANALYTICS,
      }),
    ]

    if (project.account?.tax_receipt_accreditation && (isStaff || isOwner)) {
      baseSettingsEntries.splice(
        1,
        0,
        makeMenuEntry({
          label: t('Tax reduction'),
          href: `${baseBackofficePath}${PURE_BACKOFFICE_PATHS.TAX_DEDUCTION}`,
          isActive: screen === Screen.TAX_DEDUCTION,
        }),
      )
    }

    let settingsItems: MidZoneConfigurationItem[] = baseSettingsEntries
    if (!isOnline) {
      settingsItems = [
        makeMenuEntry({
          label: t('Banking information'),
          href: `${baseBackofficePath}${PURE_BACKOFFICE_PATHS.ACCOUNT}`,
          isActive: screen === Screen.ACCOUNT,
        }),
        ...baseSettingsEntries,
      ]
    }

    if (
      isProjectDonationBased ||
      (isProjectRewardBased && projectLifecycleStatus !== models.project.LifecycleStatus.FAILURE)
    ) {
      settingsItems.push(
        makeMenuEntry({
          label: isProjectDonationBased ? t('End of fundraising') : t('End of campaign'),
          href: `${baseBackofficePath}${PURE_BACKOFFICE_PATHS.OFFBOARDING}`,
          isActive: screen === Screen.OFFBOARDING,
        }),
      )
    }

    myProjectItems.push({
      label: t('Settings'),
      isDisabled: isRefused,
      items: settingsItems,
    })
  }

  midZone.push(myProject)

  // TODO: make listing a MenuEntry by correctly handling orders reload when accessing several times this screen
  if (!isProjectMembership) {
    const listingEntry = makeMenuEntry({
      label: t('Listing'),
      href: `${baseBackofficePath}${PURE_BACKOFFICE_PATHS.ORDERS}`,
      isActive: screen === Screen.ORDERS,
    })
    midZone.push([
      {
        label: t('Contributions'),
        icon: <plume.glyphs.stroke.People size={22} />,
        isDisabled: !isOnline,
        items: [
          listingEntry,
          {
            label: t('Checks and transfers'),
            href: `/projects/${project.id}/edit/orders/offline/list/`,
            isActive: screen === Screen.OFFLINE_DONATIONS_LIST,
          },
          makeMenuEntry({
            label: t('Funds transfer'),
            href: `${baseBackofficePath}${PURE_BACKOFFICE_PATHS.ACCOUNT}`,
            isActive: screen === Screen.ACCOUNT,
          }),
        ],
      },
    ])
  } else if (isProjectMembership) {
    midZone.push([
      makeMenuEntry({
        label: t('Members'),
        href: isOngoing ? `${baseBackofficePath}${PURE_BACKOFFICE_PATHS.MEMBERS}` : undefined,
        icon: <plume.glyphs.stroke.People size={22} />,
        isActive: screen === Screen.MEMBERS,

        isDisabled: !isOnline,
      }),
    ])
  }

  midZone.push([
    makeMenuEntry({
      label: t('Posts'),
      hasPlainColorIcon: screen === Screen.NEWS,
      href: `${baseBackofficePath}${PURE_BACKOFFICE_PATHS.NEWS}`,
      icon: <plume.glyphs.stroke.News size={20} />,
      isActive: screen === Screen.NEWS,
    }),
  ])

  if (isProjectMembership) {
    midZone.push([
      makeMenuEntry({
        label: t('Revenue'),
        href: `${baseBackofficePath}${PURE_BACKOFFICE_PATHS.ACCOUNT}`,
        isActive: screen === Screen.ACCOUNT,
        hasPlainColorIcon: screen === Screen.ACCOUNT,
        icon: <plume.glyphs.stroke.Euro size={18} />,
        isDisabled: !isOnline,
      }),
    ])
  }

  if (isProjectRewardBased) {
    midZone.push([
      {
        label: t('Statistics'),
        href: isOnline ? `/projects/${project.id}/edit/dashboard/` : undefined,
        hasPlainColorIcon: screen === Screen.DASHBOARD,
        isActive: screen === Screen.DASHBOARD,
        isDisabled: !isOnline,
        icon: <plume.glyphs.stroke.TrendingUp size={20} />,
      },
    ])
  }

  const pathname = isClientSide ? window.location.pathname : `/${project.slug}/`
  midZone.push([
    {
      label: isOnline ? t('See my project') : t('Preview'),
      href:
        isRefused || !hasAccessToPreview
          ? undefined
          : pathname === `/${project.slug}/`
            ? `/${project.slug}/preview/`
            : `/${project.slug}/preview/?prev=${pathname}`,
      icon: hasAccessToPreview ? <plume.glyphs.stroke.Read size={20} /> : <plume.glyphs.stroke.Unread size={18} />,
      withArrow: hasAccessToPreview,
      isDisabled: isRefused || !hasAccessToPreview,
      tooltip: !isRefused && !hasAccessToPreview ? getTooltipText() : undefined,
    },
  ])

  function getTooltipText(): string {
    if (isProjectDonationBased) {
      return t('You must fill in at least a title, an image and your goal to preview the rendering of your page.')
    }

    if (isProjectMembership) {
      return t(
        'You must fill in at least a name, a profile picture and a paid membership to preview the rendering of your page.',
      )
    }

    return t(
      'You must fill in at least a title, an image, your goal and a reward to preview the rendering of your page.',
    )
  }

  return midZone

  function makeMenuEntry({ href, ...params }: MidZoneConfigurationItem): MidZoneConfigurationItem {
    const clickHandler =
      isPureBackoffice && href && navigate
        ? hooks.makeLinkClickHandler({
            navigate: () => {
              navigate(href.replace(`${baseBackofficePath}`, ''))
            },
          })
        : undefined

    return {
      href,
      ...params,
      onClick: clickHandler,
    }
  }
}

function ProjectStatus({ project }: { project: api.Project<'account'> }): React.ReactElement {
  const status = models.project.lifecycleStatus(project)
  if (status === models.project.LifecycleStatus.EXTENDED) {
    return (
      <ProjectStatusWrapper>
        <RoundDot color={plume.COLORS.SECONDARY_GREEN_500} />
        {t('Extended')}
      </ProjectStatusWrapper>
    )
  } else if (status === models.project.LifecycleStatus.ONGOING) {
    return (
      <ProjectStatusWrapper>
        <RoundDot color={plume.COLORS.SECONDARY_GREEN_500} />
        {t('Online')}
      </ProjectStatusWrapper>
    )
  } else if (status === models.project.LifecycleStatus.FAILURE || status === models.project.LifecycleStatus.SUCCESS) {
    return (
      <ProjectStatusWrapper>
        <RoundDot color={plume.COLORS.PRIMARY_SAND_200} />
        {t('Finished')}
      </ProjectStatusWrapper>
    )
  } else if (project.status === api.ProjectStatus.REFUSED) {
    return (
      <ProjectStatusWrapper>
        <RoundDot color={plume.COLORS.PRIMARY_SAND_200} />
        {t('Refused')}
      </ProjectStatusWrapper>
    )
  }

  return (
    <ProjectStatusWrapper>
      <RoundDot color={plume.COLORS.SECONDARY_YELLOW_500} />
      {t('In progress')}
    </ProjectStatusWrapper>
  )
}

function ProjectBaseIcon({ project }: { project: api.Project<'account'> }): React.ReactElement {
  if (project.type === 'membership') {
    return <SupporterIcon width={20} />
  } else if (project.type === 'donation') {
    return <DonationCoinIcon width={20} />
  }

  return <plume.glyphs.crowdfunding.EggFull size={20} />
}

// TODO: Move these to appropriate collection. (+ Merge DonationIcon w/ its ProjectCard counterpart)
function DonationCoinIcon(props: React.SVGAttributes<SVGElement>) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" style={{ marginTop: '1px' }} viewBox="0 0 63 76" {...props}>
      <g fill="#currentColor" clipPath="url(#a)">
        <path
          clipRule="evenodd"
          d="M27.088 73.442c2.694 1.31 5.583 2.022 8.587 2.022 14.619 0 26.51-16.855 26.51-37.509S50.294.447 35.675.447c-3.004 0-5.892.712-8.586 2.022 2.696.09 5.284.814 7.697 2.072.295-.017.591-.026.889-.026 12.383 0 22.442 14.977 22.442 33.44 0 18.464-10.059 33.44-22.442 33.44-.298 0-.594-.008-.89-.025-2.412 1.258-5 1.981-7.697 2.072Z"
          fillRule="evenodd"
        />
        <path d="M26.51 75.463C11.893 75.463 0 58.61 0 37.955 0 17.301 11.892.447 26.51.447c14.62 0 26.511 16.854 26.511 37.508 0 20.654-11.892 37.508-26.51 37.508Zm0-70.948c-12.383 0-22.442 14.977-22.442 33.44 0 18.464 10.06 33.44 22.443 33.44 12.383 0 22.442-14.976 22.442-33.44 0-18.463-10.104-33.44-22.442-33.44Z" />
        <path d="M23.605.67C32.904-.58 44.527-1.072 52.798 9.3c-1.476.09-11.803.492-11.803.492s-1.743-6.483-12.07-7.287c-1.565.58-11.266-1.029-5.32-1.833ZM21.95 74.927s10.775 2.816 20.476-.67c9.701-3.488 15.11-13.412 17.614-21.594-7.913.224-11.176.045-11.176.045s-4.65 12.115-8.986 15.916c-5.901 5.185-18.017 6.303-18.017 6.303h.09Zm-5.856-38.894 3.934-6.885 3.71 3.174 6.26-8.404c-.313-.358-.67-.716-.984-1.029-3.308-2.995-6.75-3.084-9.567-.76-2.816 2.325-4.65 6.93-5.499 11.356l2.101 2.503.045.045Z" />
        <path d="m23.65 36.57-3.622-3.8-3.8 5.945-2.503-3.397a34.63 34.63 0 0 0-.268 4.425c0 2.46.447 5.186 1.296 7.422.983 2.548 2.593 4.873 4.694 6.66 2.772 2.28 6.08 3.354 9.299 2.326 4.158-1.342 7.064-6.706 7.019-13.412 0-6.214-1.386-11.847-3.71-15.96l-8.405 9.746v.044Z" />
      </g>
      <defs>
        <clipPath id="a">
          <path d="M0 0h62.186v76H0z" />
        </clipPath>
      </defs>
    </svg>
  )
}

// FIXME: Replace with crowdfunding.Owl
function SupporterIcon(props: React.SVGAttributes<SVGElement>) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" style={{ marginTop: '1px' }} viewBox="0 0 20 21" {...props}>
      <path
        d="M18.668 5.649C17.025 2.7 13.894.709 10.3.709c-3.593 0-6.725 1.992-8.37 4.942l-.918-.874a.183.183 0 0 0-.309.136v5.371c0 5.351 4.258 9.768 9.563 9.786 5.317.018 9.631-4.324 9.631-9.68V4.97a.21.21 0 0 0-.353-.154l-.876.833Z"
        fill="currentColor"
      />
    </svg>
  )
}

const ProjectImage = styled(plume.styles.image.Avatar)`
  flex: none;

  &&& {
    margin: 0;
  }
`

const ProjectStatusWrapper = styled(plume.styles.copy.XS)`
  color: ${plume.COLORS.PRIMARY_SAND_700};
  text-transform: none;
`

const RoundDot = styled.span<{ color: string }>`
  border-radius: 50%;
  display: inline-block;
  height: 6px;
  margin-bottom: 1px;
  margin-right: 4px;
  width: 6px;

  ${({ color = plume.COLORS.PRIMARY_GREY_500 }) => {
    return css`
      background-color: ${color};
    `
  }};
`

export const ConfiguredNavMenu = styled(ConfiguredNavMenuComponent)``
